import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  Navigation, 
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Location, LocationUpdate } from '../types';
import { getCurrentLocation } from '../utils/geolocation';
import { SocketManager } from '../utils/socketManager';

interface LocationTrackerProps {
  responderId: string;
  responderName: string;
  onLocationUpdate?: (location: Location) => void;
  isTracking?: boolean;
  className?: string;
}

export const LocationTracker: React.FC<LocationTrackerProps> = ({
  responderId,
  responderName,
  onLocationUpdate,
  isTracking = false,
  className = ''
}) => {
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(isTracking);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [socketManager] = useState(() => new SocketManager());

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (trackingEnabled && isOnline) {
      // Update location every 30 seconds
      intervalId = setInterval(() => {
        updateLocation();
      }, 30000);

      // Initial location update
      updateLocation();
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [trackingEnabled, isOnline]);

  const updateLocation = async () => {
    if (!isOnline) return;

    setIsGettingLocation(true);
    try {
      const location = await getCurrentLocation();
      const newLocation: Location = {
        latitude: location.latitude,
        longitude: location.longitude,
        address: `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
      };

      setCurrentLocation(newLocation);
      setLastUpdate(new Date());
      
      // Send location update via socket
      const locationUpdate: LocationUpdate = {
        responderId,
        location: newLocation,
        timestamp: new Date()
      };

      socketManager.sendLocationUpdate(locationUpdate);
      onLocationUpdate?.(newLocation);

    } catch (error) {
      console.error('Failed to get location:', error);
    } finally {
      setIsGettingLocation(false);
    }
  };

  const toggleTracking = () => {
    setTrackingEnabled(!trackingEnabled);
  };

  const formatLastUpdate = () => {
    if (!lastUpdate) return 'Never';
    const now = new Date();
    const diffMs = now.getTime() - lastUpdate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes === 1) return '1 minute ago';
    return `${diffMinutes} minutes ago`;
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-red-600" />
          <h3 className="font-semibold text-gray-900">Location Tracking</h3>
        </div>
        <div className="flex items-center gap-2">
          {isOnline ? (
            <Wifi className="w-4 h-4 text-green-600" title="Online" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-600" title="Offline" />
          )}
          <button
            onClick={toggleTracking}
            className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${
              trackingEnabled
                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            {trackingEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-sm text-gray-600">
          <strong>Responder:</strong> {responderName}
        </div>

        {currentLocation ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Navigation className="w-4 h-4" />
              <span>
                {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
              </span>
            </div>
            {currentLocation.address && (
              <div className="text-sm text-gray-600">
                <strong>Address:</strong> {currentLocation.address}
              </div>
            )}
            <div className="text-xs text-gray-500">
              Last updated: {formatLastUpdate()}
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            No location data available
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={updateLocation}
            disabled={isGettingLocation || !isOnline}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isGettingLocation ? 'animate-spin' : ''}`} />
            {isGettingLocation ? 'Updating...' : 'Update Now'}
          </button>

          {trackingEnabled && (
            <div className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-800 text-sm rounded-lg">
              <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
              <span>Live Tracking</span>
            </div>
          )}
        </div>

        {!isOnline && (
          <div className="p-2 bg-yellow-100 text-yellow-800 text-sm rounded-lg">
            <strong>Offline:</strong> Location updates will resume when connection is restored.
          </div>
        )}
      </div>
    </div>
  );
};